<template>
  <Sheet v-model:open="isOpen">
    <SheetContent class="overflow-y-auto">
      <SheetHeader padded>
        <SheetTitle size="lg">{{ mode === 'edit' ? 'Edit custom field' : 'New custom field' }}</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex flex-col gap-5">
        <Field class="gap-1">
          <FieldLabel for="cf-name">Field name</FieldLabel>
          <Input id="cf-name" v-model="form.name" placeholder="e.g. Preferred office" />
        </Field>

        <Field class="gap-1">
          <FieldLabel for="cf-key">Field key</FieldLabel>
          <Input id="cf-key" :model-value="displayKey" readonly disabled class="font-mono text-muted-foreground" />
          <FieldDescription>
            Generated from the name and permanent, so references in documents keep working.
          </FieldDescription>
        </Field>

        <Field class="gap-1">
          <FieldLabel for="cf-type">Input type</FieldLabel>
          <Select v-model="form.field_type">
            <SelectTrigger id="cf-type">
              <SelectValue placeholder="Select input type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="fieldType in fieldTypes.list()" :key="fieldType.key" :value="fieldType.key">
                {{ fieldType.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field v-if="showOptions" class="gap-1">
          <FieldLabel for="cf-options">Options</FieldLabel>
          <Textarea id="cf-options" v-model="form.optionsText" rows="4" placeholder="One option per line" />
          <FieldDescription>Enter one option per line.</FieldDescription>
        </Field>

        <FieldSet class="gap-2">
          <FieldLegend>Applies to</FieldLegend>
          <FieldDescription>Choose who can have this field.</FieldDescription>
          <div class="mt-1 flex flex-col gap-2">
            <div v-for="scope in scopes.list()" :key="scope.key" class="flex items-center gap-2">
              <Checkbox
                :id="`cf-scope-${scope.key}`"
                :model-value="form.scopes.includes(scope.key)"
                @update:model-value="toggleScope(scope.key, $event === true)"
              />
              <Label :for="`cf-scope-${scope.key}`" class="font-normal">{{ scope.label }}</Label>
            </div>
          </div>
        </FieldSet>

        <div class="flex items-start justify-between gap-4 rounded-lg border p-4">
          <div class="space-y-0.5">
            <Label for="cf-sensitive">Sensitive</Label>
            <p class="text-xs text-muted-foreground">
              For sensitive data like logins or tax info. Can't be changed after creation.
            </p>
          </div>
          <Switch id="cf-sensitive" v-model="form.sensitive" :disabled="mode === 'edit'" />
        </div>

        <div v-if="showAutoCollect" class="flex items-start justify-between gap-4 rounded-lg border p-4">
          <div class="space-y-0.5">
            <Label for="cf-auto-collect">Auto collect during onboarding</Label>
            <p class="text-xs text-muted-foreground">Collect this from teammates automatically during onboarding.</p>
          </div>
          <Switch id="cf-auto-collect" v-model="form.auto_collect_during_onboarding" />
        </div>

        <Dialog v-if="mode === 'edit'">
          <DialogTrigger as-child>
            <Button variant="ghost" class="mt-2 w-fit gap-2 text-destructive hover:text-destructive">
              <Trash2 class="h-4 w-4" /> Delete field
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete custom field</DialogTitle>
              <DialogDescription>
                Deleting this field is permanent and removes its data from every candidate and teammate it is attached
                to. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose as-child>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button variant="destructive" :disabled="isSaving" @click="handleDelete">Delete field</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetBody>

      <SheetFooter bordered class="flex-row justify-end gap-3">
        <SheetClose as-child>
          <Button variant="outline" :disabled="isSaving">Cancel</Button>
        </SheetClose>
        <Button :disabled="isSaving" @click="handleSave">
          {{ saveLabel }}
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { Trash2 } from 'lucide-vue-next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel, FieldDescription, FieldSet, FieldLegend } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import * as fieldTypes from './fieldTypes';
import * as scopes from './scopes';
import { useCustomFieldForm } from './useCustomFieldForm';

const props = defineProps<{
  open: boolean;
  field: CustomFieldEntity | null;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  saved: [];
}>();

const isOpen = computed({
  get: () => props.open,
  set: value => emit('update:open', value),
});

const { mode, form, isSaving, derivedKey, showOptions, showAutoCollect, loadFrom, toggleScope, save, remove } =
  useCustomFieldForm();

const displayKey = computed(() => (mode.value === 'edit' ? form.value.key : derivedKey.value));
const saveLabel = computed(() => {
  if (isSaving.value) return mode.value === 'edit' ? 'Saving...' : 'Creating...';
  return mode.value === 'edit' ? 'Save changes' : 'Create field';
});

watch(
  () => props.open,
  open => {
    if (open) loadFrom(props.field);
  },
  { immediate: true }
);

const handleSave = async () => {
  const success = await save();
  if (success) {
    isOpen.value = false;
    emit('saved');
  }
};

const handleDelete = async () => {
  const success = await remove();
  if (success) {
    isOpen.value = false;
    emit('saved');
  }
};
</script>
